import React, { useMemo, useState } from 'react';
import {
   Accordion,
   AccordionDetails,
   AccordionSummary,
   Box,
   Button,
   Chip,
   Divider,
   IconButton,
   MenuItem,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   TextField,
   Tooltip,
   Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { dateToRef } from '../../utils/spaceAvailability';
import useClipboard from '../../hooks/useClipboard';

// The public, branded base URL (served via the /api/v2/** hosting rewrite).
const BASE_URL = 'https://app.intheoffice.io/api/v2';

// path: the URL suffix; params: which inputs to show ('?' suffix = optional)
const ENDPOINTS = [
   {
      value: 'schedule',
      path: '/schedule',
      scope: 'schedule',
      params: ['lid', 'date'],
      desc: "Each person's status for a location on a date (AM/PM splits, routine, offsite).",
   },
   {
      value: 'schedule/summary',
      path: '/schedule/summary',
      scope: 'schedule',
      params: ['lid', 'date'],
      desc: 'Aggregate counts (in office / WFH / away / offsite), with AM/PM breakdown.',
   },
   {
      value: 'bookings',
      path: '/bookings',
      scope: 'bookings',
      params: ['lid', 'date', 'uid?'],
      desc: 'All space bookings for a location on a date. Optional uid filters to one person.',
   },
   {
      value: 'bookings/space',
      path: '/bookings/space',
      scope: 'bookings',
      params: ['lid', 'sid', 'date'],
      desc: 'Bookings + capacity for a single space on a date.',
   },
   {
      value: 'users',
      path: '/users',
      scope: 'users',
      params: ['lid?'],
      desc: 'People directory. With lid: that location only; without: the whole company.',
   },
];

const monoBox = {
   fontFamily: 'monospace',
   fontSize: 13,
   bgcolor: 'grey.100',
   p: 1.5,
   borderRadius: 1,
   whiteSpace: 'pre-wrap',
   wordBreak: 'break-all',
};

function ApiPlayground({ company, lastCreatedKey }) {
   const copy = useClipboard();
   const locations = company?.locations || {};
   const locationEntries = Object.entries(locations);

   const [apiKey, setApiKey] = useState(lastCreatedKey || '');
   const [endpoint, setEndpoint] = useState(ENDPOINTS[0].value);
   const [lid, setLid] = useState(locationEntries[0]?.[0] || '');
   const [dateInput, setDateInput] = useState(() => {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
   });
   const [sid, setSid] = useState('');
   const [uid, setUid] = useState('');

   const [sending, setSending] = useState(false);
   const [responseStatus, setResponseStatus] = useState(null);
   const [responseText, setResponseText] = useState('');

   const current = ENDPOINTS.find((e) => e.value === endpoint) || ENDPOINTS[0];
   const showParam = (p) => current.params.includes(p) || current.params.includes(`${p}?`);
   const optionalParam = (p) => current.params.includes(`${p}?`);

   // Convert the date input (YYYY-MM-DD) to the project's zero-based-month ref.
   const dateRef = useMemo(() => {
      if (!dateInput) return '';
      const d = new Date(`${dateInput}T00:00:00`);
      if (Number.isNaN(d.getTime())) return '';
      return dateToRef(d);
   }, [dateInput]);

   const queryString = useMemo(() => {
      const qs = new URLSearchParams();
      if (showParam('lid') && lid) qs.set('lid', lid);
      if (showParam('sid') && sid) qs.set('sid', sid);
      if (showParam('date') && dateRef) qs.set('date', dateRef);
      if (showParam('uid') && uid) qs.set('uid', uid);
      const s = qs.toString();
      return s ? `?${s}` : '';
   }, [endpoint, lid, sid, dateRef, uid]); // eslint-disable-line react-hooks/exhaustive-deps

   const fullUrl = `${BASE_URL}${current.path}${queryString}`;
   const curl = `curl -H "x-api-key: ${apiKey || 'YOUR_API_KEY'}" "${fullUrl}"`;

   const send = async () => {
      if (!apiKey) return;
      setSending(true);
      setResponseStatus(null);
      setResponseText('');
      try {
         const res = await fetch(fullUrl, {
            method: 'GET',
            headers: { 'x-api-key': apiKey },
         });
         setResponseStatus(res.status);
         const body = await res.text();
         try {
            setResponseText(JSON.stringify(JSON.parse(body), null, 2));
         } catch (e) {
            setResponseText(body);
         }
      } catch (err) {
         setResponseStatus(0);
         setResponseText(
            `Request failed: ${err?.message || err}\n\n` +
               'If this is a network/CORS error, confirm the API function is deployed and publicly invocable, and that the selected base URL is reachable.',
         );
      }
      setSending(false);
   };

   const statusColor = (s) =>
      s >= 200 && s < 300 ? 'success' : s >= 400 ? 'error' : 'default';

   return (
      <Box sx={{ mt: 3 }}>
         {/* ---- Documentation ---- */}
         <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
               <Typography variant="h6">Using the API</Typography>
            </AccordionSummary>
            <AccordionDetails>
               <Typography variant="body2" gutterBottom>
                  Authenticate every request with your key in the{' '}
                  <code>x-api-key</code> header. Keys look like{' '}
                  <code>ito_&lt;companyId&gt;_&lt;secret&gt;</code> and are shown once on
                  creation. All endpoints are <strong>GET</strong> and read-only.
               </Typography>

               <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Base URL
               </Typography>
               <Box sx={monoBox}>{BASE_URL}</Box>

               <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Date format
               </Typography>
               <Typography variant="body2">
                  The <code>date</code> parameter is <code>YYYYMMDD</code> with a{' '}
                  <strong>zero-based month</strong> (January = 00 … December = 11), e.g.{' '}
                  5&nbsp;June&nbsp;2026 = <code>20260505</code>. The playground below
                  builds this for you from a date picker.
               </Typography>

               <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Endpoints
               </Typography>
               <Table size="small">
                  <TableHead>
                     <TableRow>
                        <TableCell>Endpoint</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Query params</TableCell>
                        <TableCell>Returns</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {ENDPOINTS.map((e) => (
                        <TableRow key={e.value}>
                           <TableCell>
                              <code>GET {e.path}</code>
                           </TableCell>
                           <TableCell>
                              <Chip label={e.scope} size="small" />
                           </TableCell>
                           <TableCell>{e.params.join(', ')}</TableCell>
                           <TableCell>{e.desc}</TableCell>
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
               <Typography variant="caption" color="textSecondary">
                  Params marked with “?” are optional. A key only works for the scopes and
                  locations it was granted.
               </Typography>
            </AccordionDetails>
         </Accordion>

         {/* ---- Playground ---- */}
         <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
               <Typography variant="h6">Playground</Typography>
            </AccordionSummary>
            <AccordionDetails>
               <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}
               >
                  <TextField
                     label="API key (x-api-key)"
                     size="small"
                     value={apiKey}
                     onChange={(e) => setApiKey(e.target.value)}
                     placeholder="ito_…  (paste a key — they aren't retrievable)"
                     sx={{ minWidth: 360, flexGrow: 1 }}
                  />
               </Box>

               <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, alignItems: 'flex-start' }}
               >
                  <TextField
                     select
                     label="Endpoint"
                     size="small"
                     value={endpoint}
                     onChange={(e) => setEndpoint(e.target.value)}
                     sx={{ minWidth: 220 }}
                  >
                     {ENDPOINTS.map((e) => (
                        <MenuItem key={e.value} value={e.value}>
                           GET {e.path}
                        </MenuItem>
                     ))}
                  </TextField>

                  {showParam('lid') &&
                     (locationEntries.length > 0 ? (
                        <TextField
                           select
                           label={optionalParam('lid') ? 'Location (optional)' : 'Location'}
                           size="small"
                           value={lid}
                           onChange={(e) => setLid(e.target.value)}
                           sx={{ minWidth: 220 }}
                        >
                           {optionalParam('lid') && (
                              <MenuItem value="">
                                 <em>All (company-wide)</em>
                              </MenuItem>
                           )}
                           {locationEntries.map(([id, locName]) => (
                              <MenuItem key={id} value={id}>
                                 {locName}
                              </MenuItem>
                           ))}
                        </TextField>
                     ) : (
                        <TextField
                           label="Location ID (lid)"
                           size="small"
                           value={lid}
                           onChange={(e) => setLid(e.target.value)}
                        />
                     ))}

                  {showParam('sid') && (
                     <TextField
                        label="Space ID (sid)"
                        size="small"
                        value={sid}
                        onChange={(e) => setSid(e.target.value)}
                     />
                  )}

                  {showParam('date') && (
                     <TextField
                        type="date"
                        label="Date"
                        size="small"
                        value={dateInput}
                        onChange={(e) => setDateInput(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText={dateRef ? `→ ${dateRef}` : ''}
                     />
                  )}

                  {showParam('uid') && (
                     <TextField
                        label="User ID (uid, optional)"
                        size="small"
                        value={uid}
                        onChange={(e) => setUid(e.target.value)}
                     />
                  )}
               </Box>

               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Button
                     variant="contained"
                     onClick={send}
                     disabled={sending || !apiKey}
                  >
                     {sending ? 'Sending…' : 'Send'}
                  </Button>
                  <Tooltip title="Copy request URL">
                     <IconButton size="small" onClick={() => copy(fullUrl, 'URL copied')}>
                        <ContentCopyIcon fontSize="small" />
                     </IconButton>
                  </Tooltip>
               </Box>

               <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Request URL
               </Typography>
               <Box sx={monoBox}>{fullUrl}</Box>

               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Typography variant="subtitle2">cURL</Typography>
                  <Tooltip title="Copy cURL">
                     <IconButton size="small" onClick={() => copy(curl, 'cURL copied')}>
                        <ContentCopyIcon fontSize="small" />
                     </IconButton>
                  </Tooltip>
               </Box>
               <Box sx={monoBox}>{curl}</Box>

               {responseStatus !== null && (
                  <>
                     <Divider sx={{ my: 2 }} />
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">Response</Typography>
                        <Chip
                           label={responseStatus === 0 ? 'network error' : `HTTP ${responseStatus}`}
                           size="small"
                           color={statusColor(responseStatus)}
                        />
                     </Box>
                     <Box component="pre" sx={{ ...monoBox, mt: 1, maxHeight: 360, overflow: 'auto' }}>
                        {responseText}
                     </Box>
                  </>
               )}
            </AccordionDetails>
         </Accordion>
      </Box>
   );
}

export default ApiPlayground;
